import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { DatapointExhibitionTypeState } from '../reducers/gerenciadorPrecos/datapointExhibitionType';

export type PriceManagerView = DatapointExhibitionTypeState['datapointExhibitionType'];

interface PriceManagerViewStore {
  viewsByUser: Record<string, PriceManagerView>;
  setView: (userKey: string | null, view: PriceManagerView) => void;
}

export const DEFAULT_PRICE_MANAGER_VIEW: PriceManagerView = 'PRODUCT';
export const PRICE_MANAGER_VIEW_STORAGE_KEY = 'ipa.price-manager-view-preferences';
const PRICE_MANAGER_VIEWS = new Set<PriceManagerView>(['PRODUCT', 'FAMILY', 'PRODUCT_CLUSTER', 'PRODUCT_CLUSTER_FAMILY']);

export const normalizePriceManagerUserKey = (userKey: string | null | undefined) => userKey?.trim().toLowerCase() || null;

const isPriceManagerView = (view: unknown): view is PriceManagerView => typeof view === 'string' && PRICE_MANAGER_VIEWS.has(view as PriceManagerView);

const sanitizeViewsByUser = (viewsByUser: unknown): Record<string, PriceManagerView> => {
  if (!viewsByUser || typeof viewsByUser !== 'object' || Array.isArray(viewsByUser)) return {};

  return Object.entries(viewsByUser).reduce<Record<string, PriceManagerView>>((validViews, [userKey, view]) => {
    const normalizedUserKey = normalizePriceManagerUserKey(userKey);
    if (normalizedUserKey && isPriceManagerView(view)) {
      validViews[normalizedUserKey] = view;
    }

    return validViews;
  }, {});
};

export const getCurrentPriceManagerUserKey = () => {
  try {
    const email = JSON.parse(localStorage.getItem('usuario') ?? 'null')?.email;
    return normalizePriceManagerUserKey(typeof email === 'string' ? email : null);
  } catch {
    return null;
  }
};

export const usePriceManagerViewStore = create<PriceManagerViewStore>()(
  persist(
    set => ({
      viewsByUser: {},
      setView: (userKey, view) => {
        const normalizedUserKey = normalizePriceManagerUserKey(userKey);
        if (!normalizedUserKey) return;

        set(state => ({
          viewsByUser: {
            ...state.viewsByUser,
            [normalizedUserKey]: view,
          },
        }));
      },
    }),
    {
      name: PRICE_MANAGER_VIEW_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({ viewsByUser: state.viewsByUser }),
      merge: (persistedState, currentState) => ({
        ...currentState,
        viewsByUser: sanitizeViewsByUser((persistedState as Partial<PriceManagerViewStore> | undefined)?.viewsByUser),
      }),
    },
  ),
);

export const selectPriceManagerView = (state: Pick<PriceManagerViewStore, 'viewsByUser'>, userKey: string | null | undefined): PriceManagerView => {
  const normalizedUserKey = normalizePriceManagerUserKey(userKey);
  if (!normalizedUserKey) return DEFAULT_PRICE_MANAGER_VIEW;

  const view = state.viewsByUser?.[normalizedUserKey];
  return isPriceManagerView(view) ? view : DEFAULT_PRICE_MANAGER_VIEW;
};

export const getPriceManagerView = (userKey: string | null | undefined): PriceManagerView =>
  selectPriceManagerView(usePriceManagerViewStore.getState(), userKey);
